'use client'

import { motion, useReducedMotion } from 'motion/react'
import React from 'react'

type Props = {
  children: React.ReactNode
  delay?: number
  className?: string
  /** Quando `children` é string, revela palavra a palavra. */
  porPalavra?: boolean
  'aria-hidden'?: boolean
}

const EASE = [0.22, 1, 0.36, 1] as const

/**
 * Revelação «máscara + deslocamento» (como no site de referência): o texto sobe de dentro de um
 * contentor com `overflow: clip` quando entra na viewport (uma vez). Com `prefers-reduced-motion`
 * (ou `--ag-motion: 0`) mostra direto.
 */
export function TextoRevelado({ children, delay = 0, className, porPalavra, ...rest }: Props) {
  const reduce = useReducedMotion()
  const [motionOff, setMotionOff] = React.useState(false)
  React.useEffect(() => {
    try {
      setMotionOff(getComputedStyle(document.documentElement).getPropertyValue('--ag-motion').trim() === '0')
    } catch {
      /* ignore */
    }
  }, [])

  if (reduce || motionOff) {
    return (
      <span className={className} {...rest}>
        {children}
      </span>
    )
  }

  if (porPalavra && typeof children === 'string') {
    const palavras = children.split(/(\s+)/)
    return (
      <span className={className} {...rest}>
        {palavras.map((p, i) =>
          /^\s+$/.test(p) ? (
            <React.Fragment key={i}>{p}</React.Fragment>
          ) : (
            <span key={i} className="inline-block overflow-clip align-bottom">
              <motion.span
                className="inline-block"
                initial={{ y: '110%', opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                transition={{ duration: 0.7, ease: EASE, delay: delay + i * 0.03 }}
              >
                {p}
              </motion.span>
            </span>
          ),
        )}
      </span>
    )
  }

  return (
    <span className={className} style={{ display: 'block', overflow: 'clip' }} {...rest}>
      <motion.span
        style={{ display: 'block' }}
        initial={{ y: '100%', opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
        transition={{ duration: 0.8, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  )
}
